import { useState } from 'react';
import { useTheme } from '@/hooks/useTheme';

export default function TestimonialsSection() {
  const { theme } = useTheme();
  const { badge, title, stats, items } = theme.testimonials;
  const [current, setCurrent] = useState(0);
  const testimonial = items[current] ?? items[0];

  if (!testimonial) return null;

  const titleLines = title.split('\\n');

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left: Stats */}
          <div>
            <p className="text-gold font-medium text-sm tracking-widest uppercase mb-3">{badge}</p>
            <h2 className="font-serif text-3xl lg:text-4xl font-bold text-coffee mb-8">
              {titleLines.map((line, i) => (
                <span key={i}>{line}{i < titleLines.length - 1 && <br />}</span>
              ))}
            </h2>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-6 mb-10">
              {stats.map((stat, i) => (
                <div key={i} className="p-5 bg-cream rounded-2xl">
                  <p className="font-serif text-2xl font-bold text-coffee mb-1">{stat.value}</p>
                  <p className="text-xs text-coffee-light">{stat.label}</p>
                </div>
              ))}
            </div>

            {/* Navigation */}
            <div className="flex items-center gap-3">
              {items.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setCurrent(i)}
                  className={`transition-all duration-300 rounded-full ${i === current ? 'w-8 h-2 bg-gold' : 'w-2 h-2 bg-coffee-light/30 hover:bg-coffee-light/60'}`}
                  aria-label={`Yorum ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Right: Testimonial Card */}
          <div className="bg-coffee rounded-3xl p-8 lg:p-10 relative overflow-hidden">
            {/* Decorative Quote */}
            <div className="absolute top-6 right-8 font-serif text-8xl text-white/5 leading-none select-none">&ldquo;</div>

            {/* Stars */}
            <div className="flex gap-1 mb-6">
              {[1,2,3,4,5].map(s => (
                <i key={s} className={`ri-star-fill text-sm ${s <= testimonial.rating ? 'text-gold' : 'text-white/20'}`} />
              ))}
            </div>

            {/* Text */}
            <blockquote className="font-serif text-lg lg:text-xl text-cream/90 leading-relaxed mb-8 relative z-10">
              &ldquo;{testimonial.text}&rdquo;
            </blockquote>

            {/* Author */}
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0 bg-coffee-medium">
                {testimonial.avatar_url ? (
                  <img
                    src={testimonial.avatar_url}
                    alt={testimonial.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-cream/50">
                    <i className="ri-user-line text-xl" />
                  </div>
                )}
              </div>
              <div>
                <p className="font-semibold text-cream text-sm">{testimonial.name}</p>
                <p className="text-xs text-cream/50">{testimonial.location}</p>
              </div>
              {testimonial.product_name && (
                <div className="ml-auto">
                  <span className="px-3 py-1 bg-gold/20 text-gold text-xs font-medium rounded-full">
                    {testimonial.product_name}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
